'use client';
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/app/components/PublicFooter';
import { Search, Music2, FileText, Calendar, Image, Video, Newspaper, Loader2, AlertCircle } from 'lucide-react';
import Link from 'next/link';

interface SearchResult {
  id: string;
  type: string;
  title: string;
  excerpt: string | null;
  url: string;
  imageUrl: string | null;
  date: string | null;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  music: <Music2 size={14} />,
  news: <FileText size={14} />,
  event: <Calendar size={14} />,
  gallery: <Image size={14} />,
  video: <Video size={14} />,
  press: <Newspaper size={14} />,
};

const TYPE_LABELS: Record<string, string> = {
  music: 'Music',
  news: 'News',
  event: 'Event',
  gallery: 'Gallery',
  video: 'Video',
  press: 'Press',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'music', label: 'Music' },
  { key: 'news', label: 'News' },
  { key: 'events', label: 'Events' },
  { key: 'videos', label: 'Videos' },
  { key: 'press', label: 'Press' },
  { key: 'gallery', label: 'Gallery' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function SearchContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const initialQ = searchParams.get('q') ?? '';
  const initialType = searchParams.get('type') ?? 'all';

  const [query, setQuery] = useState(initialQ);
  const [activeType, setActiveType] = useState(initialType);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searched, setSearched] = useState(false);

  const doSearch = useCallback(async (q: string, type: string, pg: number) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      setTotal(0);
      setSearched(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ q: q.trim(), type, page: String(pg), limit: '20' });
      const res = await fetch(`/api/search?${params}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(data.results ?? []);
      setTotal(data.total ?? 0);
      setSearched(true);
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialQ) doSearch(initialQ, initialType, 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    router.push(`/search?q=${encodeURIComponent(query)}&type=${activeType}`);
    doSearch(query, activeType, 1);
  };

  const handleTypeChange = (type: string) => {
    setActiveType(type);
    setPage(1);
    if (query.trim().length >= 2) {
      router.push(`/search?q=${encodeURIComponent(query)}&type=${type}`);
      doSearch(query, type, 1);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicTopbar />

      {/* Search header */}
      <section className="pt-32 pb-12 bg-background">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Search</p>
          <h1 className="text-4xl font-800 text-foreground mb-8">Find Music, Events & More</h1>

          <form onSubmit={handleSearch} className="flex gap-3 max-w-2xl">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search music, news, events, videos, press..."
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors text-sm"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading || query.trim().length < 2}
              className="px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-600 text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              Search
            </button>
          </form>

          {/* Type filters */}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => handleTypeChange(f.key)}
                className={`px-3 py-1.5 text-xs font-600 rounded-lg capitalize transition-all ${
                  activeType === f.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/70'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="pb-24 bg-background">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {error && (
            <div className="flex items-center gap-2 p-4 rounded-xl bg-red-950/30 border border-red-800/30 text-red-300 text-sm mb-6">
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          )}

          {!loading && searched && (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                {total === 0
                  ? `No results for "${query}"`
                  : `${total} result${total !== 1 ? 's' : ''} for "${query}"`}
              </p>

              {results.length === 0 ? (
                <div className="text-center py-24">
                  <Search size={48} className="text-muted-foreground/20 mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg mb-2">No results found</p>
                  <p className="text-muted-foreground/60 text-sm">Try different keywords or browse our content</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {results.map((r) => (
                    <Link key={`${r.type}-${r.id}`} href={r.url}
                      className="group p-5 rounded-xl bg-card border border-border hover:border-primary/30 transition-all hover:shadow-lg">
                      {r.imageUrl && !r.imageUrl.startsWith('data:') && (
                        <div className="aspect-video rounded-lg overflow-hidden mb-4 bg-muted">
                          <img src={r.imageUrl} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-600 uppercase tracking-wider">
                          {TYPE_ICONS[r.type]}
                          {TYPE_LABELS[r.type] ?? r.type}
                        </span>
                        {r.date && (
                          <span className="text-xs text-muted-foreground">{formatDate(r.date)}</span>
                        )}
                      </div>
                      <h3 className="text-base font-600 text-foreground mb-1 group-hover:text-primary transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                      {r.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{r.excerpt}</p>
                      )}
                    </Link>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {total > 20 && (
                <div className="flex items-center justify-center gap-3 mt-10">
                  <button
                    onClick={() => { const p = Math.max(1, page - 1); setPage(p); doSearch(query, activeType, p); }}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-muted-foreground">Page {page}</span>
                  <button
                    onClick={() => { const p = page + 1; setPage(p); doSearch(query, activeType, p); }}
                    disabled={page * 20 >= total}
                    className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && !searched && !error && (
            <div className="text-center py-24">
              <Search size={48} className="text-muted-foreground/20 mx-auto mb-4" />
              <p className="text-muted-foreground">Enter a search term to find music, news, events, videos, press, and more</p>
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    }>
      <SearchContent />
    </Suspense>
  );
}
